import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { organizationKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  showOrganizationContract,
  exportOrganizationDataContract,
  type Organization,
  type ShowOrganizationResponse,
  type UpdateOrganizationRequest
} from '@bliks-io/contracts';

/**
 * Shared query options for organization data.
 * Used by both the useOrganization hook and route-level beforeLoad guards.
 */
export const organizationQueryOptions = {
  queryKey: organizationKeys.details(),
  queryFn: async ({ signal }: { signal?: AbortSignal } = {}): Promise<Organization> => {
    const { data } = await apiClient.get<ShowOrganizationResponse>(showOrganizationContract.path, {
      signal
    });
    return data.data;
  }
};

/**
 * Fetch organization details
 *
 * @example
 * ```tsx
 * function OrganizationSettings() {
 *   const { data: org, isLoading } = useOrganization();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!org) return <NotFound />;
 *
 *   return <SettingsForm org={org} />;
 * }
 * ```
 */
export function useOrganization() {
  const { token } = useAuthStore();

  return useQuery({
    ...organizationQueryOptions,
    enabled: !!token
  });
}

/**
 * Update organization settings
 *
 * Automatically invalidates and refetches organization data after successful update.
 *
 * @example
 * ```tsx
 * function SettingsForm() {
 *   const updateOrg = useUpdateOrganization();
 *
 *   const handleSave = async (formData: UpdateOrganizationRequest) => {
 *     try {
 *       await updateOrg.mutateAsync(formData);
 *       toast.success('Settings updated!');
 *     } catch (error) {
 *       toast.error('Failed to update settings');
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSave}>
 *       <Button loading={updateOrg.isPending}>Save</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: UpdateOrganizationRequest) => {
      const { data } = await apiClient.patch<{ data: Organization }>(
        '/api/v2/organization',
        updates
      );
      return data.data;
    },
    onSuccess: () => {
      // Everything, not just the organization keys: capacityPriority and the
      // billing rate are inputs to every cached capacity binding (process list,
      // process detail, systems, dashboard, drafts). Settings saves are rare.
      queryClient.invalidateQueries();
    }
  });
}

/**
 * Upload organization logo
 *
 * DEPRECATED: Use hooks from useOrgLogoUpload.ts instead:
 * - useRequestOrgLogoUploadUrl() - Request presigned URL
 * - useConfirmOrgLogoUpload() - Confirm upload and process
 * - useDeleteOrgLogo() - Delete org logo
 *
 * @deprecated Use the hooks from '@/api/mutations/useOrgLogoUpload' instead
 */

/**
 * Export organization data as a ZIP file
 *
 * @example
 * ```tsx
 * function ExportButton() {
 *   const { data: org } = useOrganization();
 *   const exportData = useExportData();
 *
 *   const handleExport = async () => {
 *     if (!org) return;
 *
 *     try {
 *       const { blob, filename } = await exportData.mutateAsync();
 *       downloadBlob(blob, filename);
 *       toast.success('Data exported');
 *     } catch (error) {
 *       toast.error('Failed to export data');
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleExport} loading={exportData.isPending}>
 *       Export Data
 *     </Button>
 *   );
 * }
 * ```
 */
export function useExportData() {
  return useMutation({
    mutationFn: async () => {
      // The export can outlast the 30 s default, and a replay would build it again.
      const { data, headers } = await apiClient.get<Blob>(exportOrganizationDataContract.path, {
        responseType: 'blob',
        timeout: 0,
        skipRetry: true
      });
      return {
        blob: data,
        filename:
          /filename="([^"]+)"/.exec(String(headers['content-disposition']))?.[1] ?? 'export.zip'
      };
    }
  });
}
